import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { bookingApi, unwrap } from '@/lib/api/client';

/**
 * Who hosted what, over a period.
 *
 * From docs/booking-api.swagger.json: `TimeSlotsByHostReportModel` in,
 * `TimeSlotsByHostReportDto` out. The response carries the same data twice,
 * grouped both ways — by host, and by event-and-host — so switching the
 * grouping is a re-read of what is already in hand, not another request.
 */

export interface TimeSlotHostsModel {
  startDate: string;
  endDate: string;
  query: string;
}

/** `TimeSlotsByHostReportTimeSlotDto`. */
export interface HostTimeSlot {
  avatar: string | null;
  firstName: string | null;
  lastName: string | null;
  completeName: string | null;
  startTime: string | null;
  endTime: string | null;
  currentCapacity: number | null;
  maxCapacity: number | null;
}

/** `TimeSlotsByHostReportEventDto` — one event, hosted by one person. */
export interface HostEvent extends HostTimeSlot {
  eventName: string | null;
  eventNameEn: string | null;
  eventNameEs: string | null;
  timeSlots: HostTimeSlot[] | null;
}

/** `TimeSlotsByHostReportHostDto`. */
export interface ReportHost {
  avatar: string | null;
  firstName: string | null;
  lastName: string | null;
  completeName: string | null;
  events: HostEvent[] | null;
}

/** `TimeSlotsByHostReportDto`. */
export interface TimeSlotHostsReport {
  hosts: ReportHost[] | null;
  events: HostEvent[] | null;
}

export function useTimeSlotHostsReport(model: TimeSlotHostsModel) {
  return useQuery({
    queryKey: ['timeslot-hosts', model],
    queryFn: () =>
      unwrap<TimeSlotHostsReport>(
        bookingApi.post('admin/timeslots/hosts/report', model)
      ),
    placeholderData: keepPreviousData,
  });
}
